import React, { useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaFilter,
  FaImages,
  FaPenNib,
  FaPlus,
  FaRedo,
  FaShareAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { Badge, ErrorBox, Field, Options, UserOptions } from "./common";
import { MediaPicker } from "./MediaGallery";
import { errorMessage, fmtDate, nullIfBlank, numOrNull, platformLabel, toISODate } from "../dmApi";
import { ResultsMobileCard, ResultsMobileList } from "../../../components/ResultsMobile";
import { usePrompt } from "../../work/WorkUi";
import { workApiFor } from "../../work/workApi";
import { useNotify } from "../../../components/ConfirmModal";

function postTone(status) {
  const v = String(status || "").toLowerCase();
  if (/(posted|approved|live|done)/.test(v)) return "success";
  if (/(skip|fail|reject)/.test(v)) return "danger";
  if (/(review|pending|schedul)/.test(v)) return "warning";
  return "neutral";
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_BATCH = 60;

function emptyForm() {
  return {
    Start_Date: toISODate(),
    Planned_Time: "",
    Platforms: [],
    Weekdays: [],
    Weeks: "1",
    Content_Id: "",
    Content_Type: "Image",
    Caption: "",
    Media_URL: "",
    Assigned_To: "",
    Status_Id: "",
  };
}

function parseISO(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Dates for "repeat on these weekdays for N weeks" starting from the start date (inclusive). */
function plannedDates(startISO, weekdays, weeks) {
  if (!weekdays.length) return [startISO];
  const start = parseISO(startISO);
  const weekStart = new Date(start);
  weekStart.setDate(start.getDate() - start.getDay());
  const dates = [];
  for (let w = 0; w < weeks; w += 1) {
    for (const day of [...weekdays].sort()) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + w * 7 + day);
      if (d >= start) dates.push(toISODate(d));
    }
  }
  return dates;
}

function accountFor(accounts, platform) {
  const key = platform.toLowerCase();
  return accounts.find((a) => {
    const name = String(a.Platform_Name || "").toLowerCase();
    return name === key || name.includes(key) || (key === "gmb" && name.includes("google"));
  });
}

/** Organic posting calendar for one project, with multi-platform / repeat-day planning. */
function OrganicPosts({ api, meta, projectId, posts, content, accounts, onChange }) {
  const [promptUi, askPrompt] = usePrompt();
  const notify = useNotify();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [platformFilter, setPlatformFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [picking, setPicking] = useState(false);

  const approved = useMemo(() => content.filter((c) => c.Review_Status === "Approved"), [content]);
  const statuses = meta?.post_statuses || [];
  const statusByName = (name) => statuses.find((s) => String(s.Status).toLowerCase() === name.toLowerCase());
  const visible = platformFilter ? posts.filter((p) => p.Platform === platformFilter) : posts;

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const toggle = (key, value) =>
    setForm((p) => ({
      ...p,
      [key]: p[key].includes(value) ? p[key].filter((v) => v !== value) : [...p[key], value],
    }));

  const dates = plannedDates(form.Start_Date || toISODate(), form.Weekdays, Math.max(1, Number(form.Weeks) || 1));
  const platforms = form.Platforms.length ? form.Platforms : [""];
  const total = dates.length * platforms.length;

  const submit = async (e) => {
    e.preventDefault();
    if (total > MAX_BATCH) {
      setError(`That would create ${total} posts; plan at most ${MAX_BATCH} at a time.`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      for (const date of dates) {
        for (const platform of platforms) {
          const account = platform ? accountFor(accounts, platform) : null;
          // Sequential on purpose: stops at the first post the server rejects.
          await api.createPost(projectId, {
            Planned_Date: date,
            Planned_Time: nullIfBlank(form.Planned_Time),
            Platform: nullIfBlank(platform),
            Social_Account_Id: account ? account.Account_Id : null,
            Content_Id: numOrNull(form.Content_Id),
            Content_Type: form.Content_Type,
            Caption: nullIfBlank(form.Caption),
            Media_URL: nullIfBlank(form.Media_URL),
            Assigned_To: numOrNull(form.Assigned_To),
            Status_Id: numOrNull(form.Status_Id),
          });
        }
      }
      setShowForm(false);
      setForm(emptyForm());
    } catch (err) {
      setError(errorMessage(err, "Could not create posts"));
    } finally {
      setSaving(false);
      await onChange();
    }
  };

  const quickStatus = async (post, name) => {
    const status = statusByName(name);
    if (!status) return;
    const body = { Status_Id: status.Status_Id };
    if (name === "Posted") {
      const url = await askPrompt({
        title: "Mark as posted",
        label: "Live post link (optional)",
        type: "url",
        initial: post.Posted_URL || "",
        placeholder: "https://…",
        confirmLabel: "Mark posted",
      });
      if (url === null) return;
      if (url) body.Posted_URL = url;
    }
    if (name === "Skipped") {
      const reason = await askPrompt({ title: "Skip this post", label: "Why is it skipped?", required: true, confirmLabel: "Skip" });
      if (!reason) return;
      body.Skip_Reason = reason;
    }
    setError("");
    try {
      await api.updatePost(post.Post_Id, body);
      await onChange();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const trackResults = async (post) => {
    const url = await askPrompt({
      title: "Track this post's results",
      label: "Live post link",
      type: "url",
      initial: post.Posted_URL || "",
      placeholder: "https://www.instagram.com/p/…",
      hint: "Cloud Office finds the post on the connected account and syncs its views, likes and comments every hour.",
      confirmLabel: "Find post",
    });
    if (url === null) return;
    setError("");
    try {
      const res = await workApiFor(api).trackPost(post.Post_Id, url || null);
      if (["tracked", "already_tracked"].includes(res?.status)) {
        notify.success(res.message);
      } else {
        setError(res?.message || "Post not found.");
      }
      await onChange();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const remove = async (post) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.deletePost(post.Post_Id);
      await onChange();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      {promptUi}
      <div className="dmp-section-bar">
        <label className="dmp-toolbar__field dmp-section-bar__filter">
          <span className="dmp-toolbar__label">
            <FaFilter aria-hidden="true" />
            Platform
          </span>
          <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
            <Options values={meta?.organic_platforms} blank="All platforms" format={platformLabel} />
          </select>
        </label>
        <button
          type="button"
          className={`dmp-btn dmp-btn--new${showForm ? " is-open" : ""}`}
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? <FaTimes aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
          <span>{showForm ? "Close" : "Plan posts"}</span>
        </button>
      </div>
      <ErrorBox message={error} />

      {showForm ? (
        <form className="dmp-card dmp-plan-form" onSubmit={submit}>
          <h3 className="dmp-card__title">Plan posts</h3>

          <div className="dmp-form-section">
            <div className="dmp-form-section__head">
              <FaCalendarAlt aria-hidden="true" />
              Schedule
            </div>
            <div className="dmp-form-grid">
              <Field label="Start date *">
                <input type="date" required value={form.Start_Date} onChange={set("Start_Date")} />
              </Field>
              <Field label="Time">
                <input type="time" value={form.Planned_Time} onChange={set("Planned_Time")} />
              </Field>
              <Field label="Repeat for weeks">
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={form.Weeks}
                  onChange={set("Weeks")}
                  disabled={!form.Weekdays.length}
                />
              </Field>
              <Field label="Status">
                <select value={form.Status_Id} onChange={set("Status_Id")}>
                  <option value="">Draft</option>
                  {statuses.map((s) => (
                    <option key={s.Status_Id} value={s.Status_Id}>
                      {s.Status}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div className="dmp-form-section">
            <div className="dmp-form-section__head">
              <FaShareAlt aria-hidden="true" />
              Platforms
              <span className="dmp-form-section__hint">One post per platform</span>
            </div>
            <div className="dmp-pill-toggles">
              {(meta?.organic_platforms || []).map((p) => (
                <label key={p} className={`dmp-pill-toggle${form.Platforms.includes(p) ? " is-on" : ""}`}>
                  <input type="checkbox" checked={form.Platforms.includes(p)} onChange={() => toggle("Platforms", p)} />
                  {platformLabel(p)}
                </label>
              ))}
            </div>
          </div>

          <div className="dmp-form-section">
            <div className="dmp-form-section__head">
              <FaRedo aria-hidden="true" />
              Repeat on
              <span className="dmp-form-section__hint">Leave empty for a single post on the start date</span>
            </div>
            <div className="dmp-pill-toggles dmp-pill-toggles--days">
              {WEEKDAYS.map((d, i) => (
                <label key={d} className={`dmp-pill-toggle${form.Weekdays.includes(i) ? " is-on" : ""}`}>
                  <input type="checkbox" checked={form.Weekdays.includes(i)} onChange={() => toggle("Weekdays", i)} />
                  {d}
                </label>
              ))}
            </div>
          </div>

          <div className="dmp-form-section">
            <div className="dmp-form-section__head">
              <FaPenNib aria-hidden="true" />
              Post
            </div>
            <div className="dmp-form-grid">
            <Field label="Approved content">
              <select value={form.Content_Id} onChange={set("Content_Id")}>
                <option value="">None</option>
                {approved.map((c) => (
                  <option key={c.Content_Id} value={c.Content_Id}>
                    {c.Content_Type}: {c.Title}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Post type">
              <select value={form.Content_Type} onChange={set("Content_Type")}>
                <Options values={meta?.post_content_types} />
              </select>
            </Field>
            <Field label="Assign to">
              <select value={form.Assigned_To} onChange={set("Assigned_To")}>
                <UserOptions users={meta?.users} blank="Me" />
              </select>
            </Field>
            <Field label="Media URL">
              <span className="dmp-media-field">
                <input value={form.Media_URL} onChange={set("Media_URL")} placeholder="Uses the content's media if empty" />
                <button type="button" className="dmp-chip-btn" onClick={() => setPicking((v) => !v)}>
                  <FaImages aria-hidden="true" /> Pick
                </button>
              </span>
            </Field>
            {picking ? (
              <div className="dmp-field--full">
                <MediaPicker
                  api={api}
                  projectId={projectId}
                  selectedUrl={form.Media_URL}
                  onClose={() => setPicking(false)}
                  onPick={(url) => {
                    setForm((p) => ({ ...p, Media_URL: url }));
                    setPicking(false);
                  }}
                />
              </div>
            ) : null}
            <Field label="Caption" full>
              <textarea rows={3} value={form.Caption} onChange={set("Caption")} placeholder="Uses the content's text if empty" />
            </Field>
            </div>
          </div>

          <div className="dmp-form-actions dmp-form-actions--bar">
            <span className="dmp-pill dmp-pill--accent">
              Will create <strong>{total}</strong> post{total === 1 ? "" : "s"}
            </span>
            <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" className="dmp-btn" disabled={saving || total === 0}>
              {saving ? "Creating…" : "Create posts"}
            </button>
          </div>
        </form>
      ) : null}

      <div className="dmp-card">
        <h3 className="dmp-card__title">
          Planned posts
          <span className="dmp-pill">{visible.length}</span>
        </h3>

        <ResultsMobileList loading={false} emptyLabel="No posts planned yet.">
          {visible.map((p) => (
            <ResultsMobileCard
              key={p.Post_Id}
              title={platformLabel(p.Platform || p.Platform_Name) || "Post"}
              subtitle={p.Account_Name || null}
              meta={[
                p.Status ? { label: p.Status, tone: postTone(p.Status), status: true } : null,
                p.Content_Type ? { label: p.Content_Type, tone: "info" } : null,
              ].filter(Boolean)}
              fields={[
                {
                  label: "Date",
                  value: `${fmtDate(p.Planned_Date)}${p.Planned_Time ? ` · ${p.Planned_Time}` : ""}`,
                },
                { label: "Caption", value: (p.Caption || "").slice(0, 120) || null },
                { label: "Assigned", value: p.Assigned_To_Name || null },
                { label: "Skipped", value: p.Skip_Reason || null },
              ]}
              actions={
                <div className="dmp-post-actions">
                  {p.Posted_URL ? (
                    <a className="dmp-chip-btn" href={p.Posted_URL} target="_blank" rel="noreferrer">
                      <FaExternalLinkAlt aria-hidden="true" /> Live
                    </a>
                  ) : null}
                  {["Approved", "Posted", "Skipped"].map((name) => (
                    <button key={name} type="button" className="dmp-chip-btn" onClick={() => quickStatus(p, name)}>
                      {name === "Skipped" ? "Skip" : name}
                    </button>
                  ))}
                  {p.Status === "Posted" ? (
                    <button type="button" className="dmp-chip-btn" onClick={() => trackResults(p)}>
                      Track results
                    </button>
                  ) : null}
                  <button type="button" className="dmp-chip-btn dmp-chip-btn--danger" onClick={() => remove(p)}>
                    <FaTrash aria-hidden="true" /> Delete
                  </button>
                </div>
              }
            />
          ))}
        </ResultsMobileList>

        <div className="dmp-table-wrap co-desktop-table">
          <table className="dmp-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Platform</th>
                <th>Post</th>
                <th>Status</th>
                <th>Assigned</th>
                <th>Live link</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={7} className="dmp-empty">
                    No posts planned yet.
                  </td>
                </tr>
              ) : (
                visible.map((p) => (
                  <tr key={p.Post_Id}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {fmtDate(p.Planned_Date)}
                      {p.Planned_Time ? <div className="dmp-muted">{p.Planned_Time}</div> : null}
                    </td>
                    <td>
                      {platformLabel(p.Platform || p.Platform_Name) || <span className="dmp-muted">—</span>}
                      {p.Account_Name ? <div className="dmp-muted">{p.Account_Name}</div> : null}
                    </td>
                    <td style={{ maxWidth: 340 }}>
                      <Badge value="type">{p.Content_Type}</Badge> {(p.Caption || "").slice(0, 120) || <span className="dmp-muted">No caption</span>}
                      {p.Skip_Reason ? <div className="dmp-muted">Skipped: {p.Skip_Reason}</div> : null}
                    </td>
                    <td>
                      <Badge value={p.Status} />
                    </td>
                    <td>{p.Assigned_To_Name || <span className="dmp-muted">—</span>}</td>
                    <td>
                      {p.Posted_URL ? (
                        <a href={p.Posted_URL} target="_blank" rel="noreferrer">
                          Open
                        </a>
                      ) : (
                        <span className="dmp-muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="dmp-post-actions">
                        {["Approved", "Posted", "Skipped"].map((name) => (
                          <button key={name} type="button" className="dmp-chip-btn" onClick={() => quickStatus(p, name)}>
                            {name === "Skipped" ? "Skip" : name}
                          </button>
                        ))}
                        {p.Status === "Posted" ? (
                          <button type="button" className="dmp-chip-btn" onClick={() => trackResults(p)}>
                            Track results
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="dmp-chip-btn dmp-chip-btn--danger dmp-chip-btn--icon"
                          onClick={() => remove(p)}
                          title="Delete post"
                          aria-label="Delete post"
                        >
                          <FaTrash aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default OrganicPosts;
